import { Autocomplete, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { Box, Stack } from '@mui/system'
import ReplayIcon from '@mui/icons-material/Replay'
import FolderIcon from '@mui/icons-material/Folder'
import StartIcon from '@mui/icons-material/Start'
import PublishIcon from '@mui/icons-material/Publish'
import PlanDialogContent, { getUnitCriteriaLabel } from './PlanDialogContent'
import PlanPublishDialogContent from './PlanPublishDialogContent'
import PlannerProgressDisplay from './PlannerProgressDisplay'
import { useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import { SmartDeleteDialog, SmartDialog } from '../generic/utilities/SmartDialog'
import useSmartTranslation from '../generic/hooks/useSmartTranslation/useSmartTranslation'
import SmartSelect from '../generic/smartSelect/SmartSelect'
import { Delete, SaveRounded } from '@mui/icons-material'
import usePlannerAuthorization from '../generic/hooks/useAuthorization/usePlannerAuthorization'
import useLicensePlanner from '../generic/hooks/useLicensePlanner/useLicensePlanner'
import { systemValueLabel } from '../generic/systemValues/systemValueLabel'

/**
* @typedef {import("../generic/utilities/objectStructures/planner/planification").Planification} Planification Planification object
* @typedef {import("../generic/utilities/objectStructures/planner/planification").PartPlannerEdit} PartPlannerEdit PartPlannerEdit object
 * @typedef {import('../generic/utilities/objectStructures/globalStructures').FrontEndConfig} FrontEndConfig FrontEndConfig object
*/

/**
 *
 * @param {Object} props  Props object
 * @param {Planification} props.selectedFile Current selected files planification
 * @param {React.Dispatch<React.SetStateAction<Planification>>} props.setSelectedFile Current selected files planification useState setter
 * @param {boolean} props.isPlannerWorking If true, planner is already working
 * @param {Planification[]} props.publishedPlans Published plans Planification object array
 * @param {function()} props.loadPublishedPlans Method used to reload publishedPlans array
 * @param {function(string, string)} props.showAlert Method used to show an alert
 * @param {FrontEndConfig} props.config Settings object
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setIsPlannerWorking Is planner working useState setter
 * @param {Object} props.eventInfoOnClick Object with event info on click function
 */

const TimelineFooter = ({
  selectedFile,
  setSelectedFile,
  isPlannerWorking,
  setIsPlannerWorking,
  publishedPlans,
  loadPublishedPlans,
  showAlert,
  dismissAlert,
  config,
  setIsFamilyViewActive,
  isFamilyViewActive,
  calendarRef,
  plannerProgress,
  setOrderEdited,
  orderEdited,
  // Story 153, CU9/S100 (rows "Guardar", "Activar", "Planificar", "Borrar", plus the Publish/Reset
  // dialogs below): none of these writes changes `appliedPlannerFilters`, and several of them keep
  // `selectedFile.id` unchanged too (e.g. Save keeps editing the same plan), so `usePlanMetrics`'s own
  // dependency-based refetch cannot be relied on here. Called once, after each write succeeds.
  bumpPlanMetricsVersion = () => {},
  // Story 175, RN8 bis (S167): which plan was just deleted, so the Planner can close the machine detail if it
  // was describing that plan. Called once, after the deletion succeeds.
  onPlanDeleted = () => {}
}) => {
  const [planDialogOpen, setPlanDialogOpen] = useState(false)
  const [simplePlanDialogOpen, setSimplePlanDialogOpen] = useState(false)
  const [planPublishDialogOpen, setPlanPublishDialogOpen] = useState(false)
  const [publishName, setPublishName] = useState('')
  const [open, setOpen] = useState(false)
  const [openAutocomplete, setOpenAutocomplete] = useState(false)
  const [planificationToDelete, setPlanificationToDelete] = useState([])
  const { t_ } = useSmartTranslation()
  // "Current" and "Temp" are the plans Sun creates itself (PlanificationService): shown translated, compared by name.
  const planLabel = (fileName) => (fileName === 'Current' || fileName === 'Temp' ? t_(fileName) : (fileName ?? ''))
  /** @type {[String[], React.Dispatch<React.SetStateAction<String[]>>]} */
  const [planificationCriteriaTypeList, setPlanificationCriteriaTypeList] = useState(null)
  const [isValidPlannerName, setIsValidPlannerName] = useState(false)
  const isUserPlannerAuthorized = usePlannerAuthorization()
  const [showOrderChangeMessage, setShowOrderChangeMessage] = useState(false)
  const licensePlanner = useLicensePlanner()
  /**
   * @type {Settings} Settings object
   */
  const defaultPlanParameters = {
    aprox: 'metaheuristic',
    time_exec: '5',
    criteria: 'asap',
    cost_criteria: 'cost',
    priocost: '1',
    priotemp: '1',
    showErrors: false,
    unitCriteria: 'Job',
    planificationId: null,
    foForecast: false,
    temporalHorizon: '',
    temporalHorizonUnit: null
  }
  useEffect(() => {
    const getInfo = async () => {
      if (planificationCriteriaTypeList === null) {
        setPlanificationCriteriaTypeList(await apiGet('planificationCriteria'))
      }
    }
    getInfo()
  }, [planificationCriteriaTypeList])

  const [planParameters, setPlanParameters] = useState({ ...defaultPlanParameters })

  useEffect(() => {
    if (selectedFile?.id !== null && selectedFile?.id !== undefined && planParameters.planificationId !== selectedFile.id) {
      setPlanParameters({ ...planParameters, planificationId: selectedFile.id })
    }
  }, [selectedFile?.id, planParameters])

  const handleChangePublished = (event, option) => {
    setSelectedFile(option)
  }

  const onPlanDialogAccept = async () => {
    if (orderEdited) {
      if (!showOrderChangeMessage) {
        setShowOrderChangeMessage(true)
        showAlert({ severity: 'warning', title: t_('Order in details table has changed') })
      }
      setOrderEdited(false)
    } else {
      if (showOrderChangeMessage) {
        setShowOrderChangeMessage(false)
        dismissAlert()
      }
    }

    setIsPlannerWorking(true)
    if (licensePlanner === 'AIPLANNER') {
      if (!validatePlanParameters()) {
        setIsPlannerWorking(false)
        return
      }
      setPlanDialogOpen(false)
      await apiPost('planner/new/' + true, planParameters)
    } else {
      setSimplePlanDialogOpen(false)
      await apiPost('planner/new/' + false, planParameters)
    }
    // Sometimes when an existing planification in this case Temp doesn't have time to update to the new unit criteria, on first display it's displaying wrong data
    publishedPlans.find(plan => plan.fileName === 'Temp').unitCriteria.name = planParameters.unitCriteria // system-value: raw-ok
    setSelectedFile(publishedPlans.find(plan => plan.fileName === 'Temp'))
    // S100 ("pulsa Planificar"): both branches above end up re-planning "Temp" in place, so there is
    // no guarantee `selectedFile.id` changes. The AI path is still running server-side at this point
    // (`isPlannerWorking` stays true) -- Planner.jsx's `planner/working` poller bumps again once it
    // actually finishes; this call already covers the synchronous path end to end.
    bumpPlanMetricsVersion()
  }

  const onPlanPublishDialogAccept = async () => {
    setPlanPublishDialogOpen(false)
    await apiPost('planner/publish/planification', { currentPlanificationId: selectedFile.id, planificationName: publishName })
      .then((response) => {
        showAlert({ severity: 'success' })
        setSelectedFile(response)
        loadPublishedPlans()
        // S100/S102 ("pulsa Publicar"): the published plan is the version Dirección will see.
        bumpPlanMetricsVersion()
      }).catch((e) => {
        showAlert({ severity: 'error', title: t_(e.message), message: null })
      })
    setPublishName('')
    setIsValidPlannerName(false)
  }

  function validatePlanParameters () {
    if (planParameters.priocost === '' || parseInt(planParameters.priocost) < 1 || parseInt(planParameters.priocost) > 10) {
      setPlanParameters({ ...planParameters, showErrors: true })
      return false
    }
    if (planParameters.priotemp === '' || parseInt(planParameters.priotemp) < 1 || parseInt(planParameters.priotemp) > 10) {
      setPlanParameters({ ...planParameters, showErrors: true })
      return false
    }
    if ((planParameters.time_exec === '' || parseInt(planParameters.time_exec) < 1)) {
      setPlanParameters({ ...planParameters, showErrors: true })
      return false
    }
    return true
  }
  const onReset = async () => {
    dismissAlert()
    setIsPlannerWorking(true)
    const response = await apiGet('planner/resetPlan/' + selectedFile.id)
    setSelectedFile(response)
    loadPublishedPlans()
    setIsPlannerWorking(false)
    // S100/S102 ("pulsa Reiniciar"): the figures fall back to the last published version.
    bumpPlanMetricsVersion()
  }
  const activatePlanification = async () => {
    await apiPut('planner/activate')
    setSelectedFile(publishedPlans.find(plan => plan.fileName === 'Current'))
    loadPublishedPlans()
    // S100 ("pulsa Activar")
    bumpPlanMetricsVersion()
  }

  const saveCurrentPlanModifications = async () => {
    setIsPlannerWorking(true)
    setSelectedFile(await apiPut('planner/update/' + selectedFile.id))
    setIsPlannerWorking(false)
    // S100 ("pulsa Guardar"): saves in place, `selectedFile.id` does not change.
    bumpPlanMetricsVersion()
  }
  const handleCloseDialog = () => {
    setOpen(false)
    setOpenAutocomplete(true)
  }
  const handleDeleteDialog = () => {
    setOpen(false)
    deletePlanification()
  }
  const deletePlanification = async () => {
    await apiDelete('planner/delete/' + planificationToDelete.id)
    onPlanDeleted(planificationToDelete.id)
    loadPublishedPlans()
    setSelectedFile(publishedPlans.find(plan => plan.fileName === 'Temp'))
    setOpenAutocomplete(true)
    // S100 ("borra el plan")
    bumpPlanMetricsVersion()
  }
  const columns = [
    {
      name: t_('Name'),
      field: 'fileName'
    }, {
      name: t_('Cost criteria'),
      field: 'costCriteria.name',
      // Story 204 — S88 (RN2.10): the seeded criteria read by their code (planification_criteria).
      render: (row) => systemValueLabel(t_, 'planification_criteria', row.costCriteria)
    }, {
      name: t_('Cost priority'),
      field: 'costPriority'
    }, {
      name: t_('Temporal criteria'),
      field: 'temporalCriteria.name',
      render: (row) => systemValueLabel(t_, 'planification_criteria', row.temporalCriteria)
    }, {
      name: t_('Temporal priority'),
      field: 'temporalPriority'
    }, {
      name: t_('Unit criteria'),
      field: 'unitCriteria.name',
      render: (row) => systemValueLabel(t_, 'planification_criteria', row.unitCriteria)
    }
  ]
  const handleConfirmationDialog = (event, option) => {
    // Prevent default li planification change event
    event.stopPropagation()
    event.preventDefault()
    setPlanificationToDelete(option)
    setOpen(true)
    setOpenAutocomplete(false)
  }

  useEffect(() => {
    if (!planificationCriteriaTypeList) {
      return
    }
    const newMode = planificationCriteriaTypeList.find((planificationCriteria) => planificationCriteria?.toLowerCase()?.localeCompare(config?.plannerMode?.toLowerCase()) === 0)

    if (config?.plannerMode?.toLowerCase().localeCompare('BOTH'.toLowerCase()) !== 0 && newMode !== planParameters?.unitCriteria) {
      setPlanParameters((oldParameters) => { return { ...oldParameters, unitCriteria: newMode } })
    }
  }, [config, planificationCriteriaTypeList, planParameters])

  const handleRestoreColors = () => {
    const calendarApi = calendarRef.current.getApi()
    const events = calendarApi.getEvents()

    events.forEach(event => {
      event.setProp('backgroundColor', event.extendedProps.originalColor)
      event.setProp('borderColor', event.extendedProps.originalColor)
    })

    setIsFamilyViewActive(false)
  }

  return (
    <>
      <SmartDialog
        title={{ render: <Typography variant='h5'>{t_('Start planification')}</Typography>, icon: <StartIcon fontSize='large' /> }}
        message={t_('Are you sure you want to create a new planification?')}
        setOpen={simplePlanDialogOpen}
        renderComponent={
          <Stack justifyContent='space-around' direction='row'>
            <div style={{ display: 'grid', gridTemplateColumns: '12rem max-content', gap: '3rem', width: 'max-content' }}>
              <SmartSelect
                disabled={config?.plannerMode?.toLowerCase().localeCompare('BOTH'.toLowerCase()) !== 0}
                value={planParameters.unitCriteria}
                selectableOptions={planificationCriteriaTypeList}
                onChange={(newValue) => setPlanParameters({ ...planParameters, unitCriteria: newValue })}
                label={t_('Select planning unit')}
                optionLabelTransform={(option) => getUnitCriteriaLabel(option, t_)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={planParameters.foForecast}
                    onChange={(event) => setPlanParameters({ ...planParameters, foForecast: event.target.checked })}
                  />
                  }
                label={t_('Fabrication order forecast')}
              />
              <TextField
                value={planParameters.temporalHorizon}
                InputLabelProps={{ shrink: true }}
                label={t_('Time Horizon')}
                onChange={(event) => {
                  if (isNaN(event.target.value)) {
                    return
                  }
                  if (event.target.value === '' || event.target.value === null) {
                    setPlanParameters({ ...planParameters, temporalHorizon: '', temporalHorizonUnit: null })
                    return
                  }
                  if (event.target.value < 0) {
                    setPlanParameters({ ...planParameters, temporalHorizon: 0 })
                    return
                  }

                  if (planParameters.temporalHorizonUnit === null) {
                    setPlanParameters({ ...planParameters, temporalHorizon: event.target.value, temporalHorizonUnit: 'MONTH' })
                    return
                  }
                  setPlanParameters({ ...planParameters, temporalHorizon: event.target.value })
                }}
                sx={{ alignSelf: 'center' }}
              />

              <RadioGroup
                name='radio-buttons-group'
                value={planParameters.temporalHorizonUnit}
                onChange={(event) => {
                  if (planParameters.temporalHorizon === null || planParameters.temporalHorizon === '') {
                    return
                  }
                  setPlanParameters({ ...planParameters, temporalHorizonUnit: event.target.value })
                }}
              >
                <FormControlLabel value='DAY' control={<Radio />} label={t_('Days')} />
                <FormControlLabel value='WEEK' control={<Radio />} label={t_('Weeks')} />
                <FormControlLabel value='MONTH' control={<Radio />} label={t_('Months')} />
              </RadioGroup>
            </div>
          </Stack>
        }
        cancelCallback={() => {
          setSimplePlanDialogOpen(false)
        }}
        acceptCallback={onPlanDialogAccept}
      />
      <SmartDialog
        title={{ render: <Typography variant='h5'>{t_('Start planification')}</Typography>, icon: <StartIcon fontSize='large' /> }}
        setOpen={planDialogOpen}
        cancelCallback={() => {
          setPlanDialogOpen(false)
          setPlanParameters({ ...defaultPlanParameters })
        }}
        acceptCallback={onPlanDialogAccept}
        renderComponent={
          <PlanDialogContent
            setPlanParameters={setPlanParameters}
            planParameters={planParameters}
            planificationCriteriaTypeList={planificationCriteriaTypeList}
            disableUnitSelector={config?.plannerMode?.toLowerCase().localeCompare('BOTH'.toLowerCase()) !== 0}
            plannerMode={config?.plannerMode?.toLowerCase()}
          />
        }
        paperWidth='40rem'
      />
      <SmartDialog
        title={{ render: <Typography variant='h5'>{t_('Publish planification')}</Typography>, icon: <PublishIcon fontSize='large' /> }}
        setOpen={planPublishDialogOpen}
        accept={false}
        cancelCallback={() => { setPlanPublishDialogOpen(false); setPublishName(''); setIsValidPlannerName(false) }}
        acceptCallback={onPlanPublishDialogAccept}
        renderCustomButtons={
          <Button disabled={!isValidPlannerName} onClick={onPlanPublishDialogAccept} variant='outlined' color={isValidPlannerName ? 'primary' : undefined}>
            <Typography color={isValidPlannerName ? 'primary' : 'grey'}>{t_('ACCEPT')} </Typography>
            <SaveRounded color={isValidPlannerName ? 'primary' : 'disabled'} sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
          </Button>
        }
        renderComponent={<PlanPublishDialogContent setPublishName={setPublishName} publishedPlans={publishedPlans} publishName={publishName} setIsValidPlannerName={setIsValidPlannerName} />}
      />

      <Stack direction='row' justifyContent='space-between' sx={{ marginTop: '2rem' }}>
        {isPlannerWorking
          ? (
            <Stack />)
          : (
            <Stack direction='row' gap={2}>
              <Stack>
                <Autocomplete
                  open={openAutocomplete}
                  onOpen={() => {
                    setOpenAutocomplete(true)
                  }}
                  onClose={() => setOpenAutocomplete(false)}
                  size='small'
                  value={Object.keys(selectedFile).length > 0 ? selectedFile : null}
                  onChange={handleChangePublished}
                  getOptionLabel={(option) => planLabel(option.fileName)}
                  isOptionEqualToValue={(option, value) => option.fileName === value.fileName}
                  sx={{ minWidth: '10rem' }}
                  fullWidth
                  disableClearable
                  disableCloseOnSelect
                  renderInput={(params) =>
                    <TextField
                      {...params}
                      label=''
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position='start' disablePointerEvents>
                            <FolderIcon />
                          </InputAdornment>
                        )
                      }}
                    />}
                  options={publishedPlans}
                  renderOption={(props, option) =>
                    <>
                      <Box component='li' {...props}>
                        <Box
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {planLabel(option.fileName)}
                        </Box>
                        {option?.fileName !== 'Temp' && option?.fileName !== 'Current'
                          ? (
                            <IconButton
                              color='primary' sx={{ p: '10px' }} aria-label='directions' onClick={(event) => {
                                handleConfirmationDialog(event, option)
                              }}
                            >
                              <Delete />
                            </IconButton>)
                          : null}
                      </Box>
                    </>}
                />
              </Stack>
              <Button variant='outlined' onClick={() => setPlanPublishDialogOpen(true)}>
                {t_('Save As') + '...'}
              </Button>
              <Button variant='outlined' onClick={() => saveCurrentPlanModifications()} disabled={selectedFile != null ? !selectedFile.editing || !selectedFile.canSave : true}>
                {t_('Save')}
              </Button>
            </Stack>)}
        <Stack direction='row' gap={2}>
          {isPlannerWorking
            ? (
              <PlannerProgressDisplay plannerProgress={plannerProgress} />)
            : (
              <Stack direction='row' gap={2}>
                <Button
                  disabled={!isFamilyViewActive}
                  variant='outlined'
                  onClick={
                  () => handleRestoreColors()
                  }
                >
                  {t_('Disable family view')}
                </Button>
                <Button
                  variant='outlined'
                  endIcon={<StartIcon />}
                  disabled={!isUserPlannerAuthorized('PLAN')}
                  onClick={() => licensePlanner === 'AIPLANNER' ? setPlanDialogOpen(true) : setSimplePlanDialogOpen(true)}
                  {...(selectedFile != null && selectedFile.editing === true && selectedFile.fileName !== 'Current' ? { color: 'warning' } : {})}
                >
                  {t_('Plan')}
                </Button>
                <Button variant='outlined' endIcon={<ReplayIcon />} onClick={onReset} disabled={selectedFile != null || !selectedFile.editing || selectedFile.fileName === 'Current'}>
                  {t_('Reset')}
                </Button>
                <Button variant='outlined' endIcon={<PublishIcon />} onClick={activatePlanification} disabled={!selectedFile || !selectedFile.canActivate || (!isUserPlannerAuthorized('ACTIVATE'))}>
                  {t_('Activate')}
                </Button>
              </Stack>)}
        </Stack>
      </Stack>
      <SmartDeleteDialog rows={[planificationToDelete]} columns={columns} show={open} cancelCallback={handleCloseDialog} deleteCallBack={handleDeleteDialog} />
    </>
  )
}
export default TimelineFooter
